// frontend/src/pages/TankShotsPage.tsx
// Tank Shots (/livestock/tank-shots?tank={tankId}, design 6.2): whole-tank photos,
// newest first under month headings, opened full screen to edit, delete or make
// the tank's cover. The tank in the URL is selected on arrival (useTankParam).
// Compare enters select mode (design 7.1): pick two shots, then Compare.
// Shared links (design 8.6) list the tank's live public links, when it has any.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  AspectRatio,
  Box,
  Button,
  Container,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useIntersection, useWindowEvent } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconContainer,
  IconPhotoPlus,
} from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { EmptyState } from '@/components/common/EmptyState';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRefetchOnImageError } from '@/hooks/usePhotoInbox';
import { useTankPhoto, useTankPhotoList } from '@/hooks/useTankPhotos';
import { AddPhotosButton } from '@/features/livestock/AddPhotosButton';
import { CompareButton } from '@/features/livestock/CompareButton';
import { CompareSelectBar } from '@/features/livestock/CompareSelectBar';
import { HEADER_BUTTON_HEIGHT } from '@/features/livestock/constants';
import { compareReason, orderPicks, pickShot } from '@/features/livestock/compareShots';
import {
  IMAGE_SIZES,
  TANK_SHOTS_IMAGE_SIZE_KEY,
  getStoredImageSize,
  setStoredImageSize,
  type ImageSize,
} from '@/features/livestock/imageSize';
import {
  TankShotGrid,
  type PickLabel,
  type TankShotSelection,
} from '@/features/livestock/TankShotGrid';
import { SharedLinksSection } from '@/features/livestock/SharedLinksSection';
import { TankShotViewer } from '@/features/livestock/TankShotViewer';
import { useTankParam } from '@/features/livestock/useTankParam';
import type { TankPhoto } from '@/types/tankPhotos';

function GridSkeleton(): JSX.Element {
  return (
    <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing={{ base: 8, sm: 'sm' }}>
      {Array.from({ length: 8 }, (_, i) => (
        <AspectRatio key={i} ratio={4 / 3}>
          <Skeleton radius="md" />
        </AspectRatio>
      ))}
    </SimpleGrid>
  );
}

export function TankShotsPage(): JSX.Element {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const switchingTank = useTankParam();
  const tankId = switchingTank ? undefined : selectedTank?.id;
  const list = useTankPhotoList(tankId);
  const onImageError = useRefetchOnImageError(list.refetch);
  const [size, setSize] = useState<ImageSize>(() => getStoredImageSize(TANK_SHOTS_IMAGE_SIZE_KEY));
  // The open shot is in the URL (?shot=), so the back gesture closes the viewer
  // and a link opens it.
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const shotParam = params.get('shot');
  const openId = shotParam && /^\d+$/.test(shotParam) ? Number(shotParam) : null;
  // Opened from this page (a history entry was pushed), as opposed to a link.
  const pushedShot = useRef(false);

  // Load the next page before the end of the grid scrolls into view.
  const { ref: endRef, entry } = useIntersection({ rootMargin: '600px' });
  const nearEnd = entry?.isIntersecting ?? false;
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = list;
  // Not again after a page failed (the end stays in view): Try again asks for it.
  useEffect(() => {
    if (nearEnd && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [nearEnd, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const pages = list.data?.pages;
  const shots = useMemo(() => pages?.flatMap((page) => page.items) ?? [], [pages]);
  const total = pages?.[0]?.total ?? shots.length;
  // The open shot comes from the list, so an edit or a cover change shows in it; a
  // linked shot not on the loaded pages is fetched on its own.
  const listedShot = useMemo(
    () => (openId === null ? null : shots.find((shot) => shot.id === openId) ?? null),
    [openId, shots]
  );
  const single = useTankPhoto(
    tankId,
    openId !== null && list.isSuccess && !listedShot ? openId : undefined
  );
  const openShot = listedShot ?? (single.data?.id === openId ? single.data : null);

  const open = useCallback(
    (shot: TankPhoto) => {
      pushedShot.current = true;
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('shot', String(shot.id));
        return next;
      });
    },
    [setParams]
  );
  const close = useCallback(() => {
    if (pushedShot.current) {
      pushedShot.current = false;
      navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('shot');
        return next;
      },
      { replace: true }
    );
  }, [navigate, setParams]);

  // Closed by the back gesture: the entry is gone already.
  useEffect(() => {
    if (openId === null) pushedShot.current = false;
  }, [openId]);

  // A shot that isn't there (deleted, or a bad link) closes the viewer.
  useEffect(() => {
    const gone = list.isSuccess && !listedShot && (single.isError || shotParam !== null && openId === null);
    if (shotParam !== null && gone) close();
  }, [shotParam, openId, listedShot, list.isSuccess, single.isError, close]);

  // Select mode: picks are labelled Before and After by date, not by tap order.
  const [selecting, setSelecting] = useState(false);
  const [picks, setPicks] = useState<number[]>([]);
  const ordered = useMemo(() => orderPicks(picks, shots), [picks, shots]);
  const pick = useCallback((shot: TankPhoto) => setPicks((current) => pickShot(current, shot.id)), []);
  const selection = useMemo<TankShotSelection | undefined>(() => {
    if (!selecting) return undefined;
    const labelOf = (id: number): PickLabel | null => {
      if (!picks.includes(id)) return null;
      if (!ordered) return 'Selected';
      return id === ordered.beforeId ? 'Before' : 'After';
    };
    return { labelOf, onPick: pick };
  }, [selecting, picks, ordered, pick]);
  const stopSelecting = useCallback((): void => {
    setSelecting(false);
    setPicks([]);
  }, []);
  // Another tank picked meanwhile: its shots aren't the ones being picked from.
  const selectedTankId = selectedTank?.id;
  const selectingIn = useRef(selectedTankId);
  useEffect(() => {
    if (selectingIn.current === selectedTankId) return;
    selectingIn.current = selectedTankId;
    stopSelecting();
  }, [selectedTankId, stopSelecting]);
  useWindowEvent('keydown', (event) => {
    if (!selecting || event.key !== 'Escape' || event.defaultPrevented) return;
    // Escape in an open modal (the viewer, a menu's dialog) closes that, not select mode.
    if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
    stopSelecting();
  });
  // A picked shot deleted meanwhile is no longer picked.
  useEffect(() => {
    if (picks.some((id) => !shots.some((shot) => shot.id === id))) {
      setPicks((current) => current.filter((id) => shots.some((shot) => shot.id === id)));
    }
  }, [picks, shots]);

  const changeSize = (value: string): void => {
    const next = value as ImageSize;
    setSize(next);
    setStoredImageSize(next, TANK_SHOTS_IMAGE_SIZE_KEY);
  };

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to see its tank shots" />
      </Container>
    );
  }

  const empty = list.isSuccess && shots.length === 0 && !hasNextPage;

  const actions = !empty && list.isSuccess && (
    <Group gap="xs" wrap="nowrap" grow={isMobile}>
      <CompareButton
        total={total}
        selecting={selecting}
        onSelect={() => {
          setPicks([]);
          setSelecting(true);
        }}
      />
      <AddPhotosButton assignTo="tank">
        {(props): JSX.Element => (
          <Button
            {...props}
            variant="light"
            leftSection={<IconPhotoPlus size={16} />}
            h={HEADER_BUTTON_HEIGHT}
          >
            Add Photos
          </Button>
        )}
      </AddPhotosButton>
    </Group>
  );

  return (
    <Container size="xl" py={{ base: 'md', sm: 'xl' }} px={{ base: 'xs', sm: 'md' }}>
      <Stack gap="lg">
        <Group justify="space-between" wrap="nowrap" align="center">
          <Group gap="xs" wrap="nowrap" miw={0} align="flex-start">
            <ActionIcon
              component={Link}
              to="/livestock"
              variant="subtle"
              color="gray"
              size={isMobile ? 44 : 'lg'}
              aria-label="Back to livestock"
            >
              <IconArrowLeft size={16} />
            </ActionIcon>
            <Box visibleFrom="sm" style={{ display: 'flex', flexShrink: 0, paddingTop: 4 }}>
              <IconContainer size={24} color="var(--mantine-color-teal-6)" />
            </Box>
            <Box miw={0}>
              <Title order={1} fz={{ base: 24, sm: 30 }} lh={1.2}>
                Tank Shots
              </Title>
              <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {list.isSuccess && !empty
                  ? `${selectedTank.name} · ${total} ${total === 1 ? 'shot' : 'shots'}`
                  : selectedTank.name}
              </Text>
            </Box>
          </Group>
          {!isMobile && actions}
        </Group>
        {/* On a phone the labelled buttons get their own row under the title. */}
        {isMobile && actions}

        {list.isSuccess && total === 1 && (
          <Text size="sm" c="dimmed">
            {compareReason(total)}: take one from the same spot next month.
          </Text>
        )}

        {list.isError && !isFetchNextPageError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Tank shots didn't load">
            <Group gap="sm">
              <Text size="sm">{list.error.message}</Text>
              <Button
                size="compact-sm"
                variant="light"
                color="red"
                onClick={() => void list.refetch()}
              >
                Try again
              </Button>
            </Group>
          </Alert>
        )}

        {(list.isLoading || switchingTank) && <GridSkeleton />}

        {empty && (
          <EmptyState
            icon={IconContainer}
            title="No tank shots yet"
            description="Take a tank shot from the same spot each month to see how your reef grows in"
          >
            <AddPhotosButton assignTo="tank">
              {(props): JSX.Element => (
                <Button {...props} size="md" leftSection={<IconPhotoPlus size={16} />}>
                  Add Photos
                </Button>
              )}
            </AddPhotosButton>
            <Text size="xs" c="dimmed" maw={300}>
              Photos you add here go straight to Tank Shots.
            </Text>
          </EmptyState>
        )}

        {shots.length > 0 && (
          <>
            <Group justify="flex-end">
              <SegmentedControl
                size={isMobile ? 'md' : 'xs'}
                value={size}
                onChange={changeSize}
                data={IMAGE_SIZES}
                aria-label="Image size"
              />
            </Group>
            <TankShotGrid
              shots={shots}
              size={size}
              onOpen={open}
              onImageError={onImageError}
              selection={selection}
            />
          </>
        )}

        {hasNextPage && (
          <Group justify="center" gap="xs" ref={endRef}>
            {isFetchNextPageError && <Text size="sm">More tank shots didn&apos;t load.</Text>}
            <Button
              variant="subtle"
              loading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchNextPageError ? 'Try again' : 'Load more'}
            </Button>
          </Group>
        )}

        {/* At the foot (design 6.2, 8.6); hidden while the tank has no live links. */}
        {tankId && <SharedLinksSection tankId={tankId} />}
      </Stack>

      {selecting && shots.length > 0 && (
        <CompareSelectBar
          selected={picks.length}
          isMobile={isMobile}
          onCancel={stopSelecting}
          onCompare={() => {
            // Two picks compare those; none opens First vs Latest.
            const search = new URLSearchParams({ tank: selectedTank.id });
            if (ordered) {
              search.set('before', String(ordered.beforeId));
              search.set('after', String(ordered.afterId));
            }
            navigate(`/livestock/tank-shots/compare?${search.toString()}`);
          }}
        />
      )}

      <TankShotViewer tankId={selectedTank.id} shot={openShot} onClose={close} />
    </Container>
  );
}
